import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * Custom hook for loading and managing stores data
 * Provides single source of truth for store loading with fallback
 *
 * @param {Function} apiCall - API call function from useAuth or useOutletContext
 * @returns {object} Stores array, loading state, and error
 *
 * @example
 * const { stores, loading, error } = useStores(apiCall);
 */
export const useStores = (apiCall) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!apiCall) {
      setLoading(false);
      return;
    }

    const loadStores = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiCall(`${API_BASE_URL}/stores`);
        if (response?.data) {
          setStores(response.data);
        } else {
          // Fallback to default stores if API returns no data
          setStores(getDefaultStores());
        }
      } catch (err) {
        console.error('Error loading stores:', err);
        setError(err.message || 'Failed to load stores');
        // Fallback to hardcoded stores if API fails
        setStores(getDefaultStores());
      } finally {
        setLoading(false);
      }
    };

    loadStores();
  }, [apiCall]);

  return {
    stores,
    loading,
    error,
  };
};

/**
 * Get default/fallback stores
 * Used when API is unavailable
 */
const getDefaultStores = () => [
  { id: 1, name: 'Downtown Indianapolis' },
  { id: 2, name: 'North Location' },
  { id: 3, name: 'South Location' },
  { id: 4, name: 'West Location' },
];
